import { createListCollection, Text, VStack } from '@chakra-ui/react';
import { BlastSectorOption } from '@components/map/popup/engagement/EngagementSelector';
import { Checkbox } from '@components/ui/checkbox/Checkbox';
import { CustomSelect } from '@components/ui/select/Select';
import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { usePostMajesCommandMutation } from '@services/c2/c2.api';
import { getJammingCommand } from '@utils/sensors/neutral/neutral.utils';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Dfb6ChangeConfigurationCommand, Mc2CommandEnum } from '@/types/c2/c2.types';

interface BlastEngagementActionsProps {
  blastConfigAndStatus: BlastSectorOption;
  forceActiveMode: boolean;
  onForceActiveModeChange: (value: boolean) => void;
  selectedSector: BlastSectorOption;
  isJamming: boolean;
  targetId?: string | null;
}

export default function BlastEngagementActions({
  blastConfigAndStatus,
  forceActiveMode,
  onForceActiveModeChange,
  selectedSector,
  isJamming,
  targetId,
}: Readonly<BlastEngagementActionsProps>) {
  const [postMajesCommand] = usePostMajesCommandMutation();
  const activeSite = useAppSelector(selectActiveSite);
  const { formatMessage } = useIntl();

  const configs = createListCollection({
    items: blastConfigAndStatus.monitoring.configuration.configs.map((config) => ({
      label: config.configName,
      value: config.configId.toString(),
      preferred: config.configId.toString() === blastConfigAndStatus.monitoring.status.preferredConfigId?.toString(),
    })),
  });

  const [selectedConfig, setSelectedConfig] = useState<string | undefined>(
    blastConfigAndStatus.monitoring.configuration.currentConfig?.toString(),
  );

  const [isLoading, setIsLoading] = useState(false);

  const isPreferredConfig = selectedConfig === blastConfigAndStatus.monitoring.status.preferredConfigId?.toString();

  useEffect(() => {
    if (!isJamming) {
      postMajesCommand({
        appCode: selectedSector.monitoring.configuration.appCode,
        site: activeSite?.code ?? '',
        command: {
          type: Mc2CommandEnum.ChangeConfiguration,
          sensorCode: selectedSector.monitoring.configuration.code,
          targetId,
        } as Dfb6ChangeConfigurationCommand,
      });
    }
  }, [
    activeSite?.code,
    postMajesCommand,
    selectedSector.monitoring.configuration.appCode,
    selectedSector.monitoring.configuration.code,
    targetId,
  ]);

  useEffect(() => {
    setSelectedConfig(blastConfigAndStatus.monitoring.configuration.currentConfig?.toString());
    if (isLoading) {
      setIsLoading(false);
    }
  }, [blastConfigAndStatus.monitoring.configuration.currentConfig]);

  const handleScenarioChange = (selected: string | null) => {
    if (selected === null) {
      return;
    }

    setIsLoading(true);

    postMajesCommand({
      appCode: selectedSector.monitoring.configuration.appCode,
      site: activeSite?.code ?? '',
      command: {
        type: Mc2CommandEnum.ChangeConfiguration,
        sensorCode: selectedSector.monitoring.configuration.code,
        configuration: selected,
        targetId,
      } as Dfb6ChangeConfigurationCommand,
    })
      .unwrap()
      .catch(() => setIsLoading(false));
  };

  const handleChangeJammingMode = () => {
    onForceActiveModeChange(!forceActiveMode);
    if (isJamming) {
      const toastParams = {
        command: 'sensors.commands.neutral.jammingCommand',
        name: selectedSector.monitoring.configuration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });

      const jammingCommand = getJammingCommand(selectedSector, Mc2CommandEnum.BlastJamming, !forceActiveMode);

      postMajesCommand({
        appCode: selectedSector.monitoring.configuration.appCode,
        site: activeSite?.code ?? '',
        command: jammingCommand,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };
  //TODO HLF#59958 add something for synchro mode selection (maquette)
  return (
    <VStack gap={3} boxSize="100%" alignItems="start">
      <VStack gap={1.5} boxSize="100%" alignItems="start">
        <Text textStyle="md" color="neutral.300">
          <FormattedMessage id="sensors.tab.commands.blast.scenario" />
        </Text>

        <CustomSelect
          disabled={isJamming}
          collection={configs}
          value={selectedConfig}
          onChange={handleScenarioChange}
          loading={isLoading}
          formatLabel={(option) => (
            <Text>
              {`${option.label} ${
                option.preferred ? formatMessage({ id: 'sensors.tab.commands.blast.preferred' }) : ''
              }`}
            </Text>
          )}
        />
        {isPreferredConfig && (
          <Text textStyle="sm" color="green.300">
            <FormattedMessage id="sensors.tab.commands.blast.preferredScenario" />
          </Text>
        )}
      </VStack>
      <Checkbox
        disabled={selectedConfig === undefined}
        checked={forceActiveMode}
        label={
          <Text>
            <FormattedMessage id="sensors.tab.commands.blast.forceActiveMode" />
          </Text>
        }
        //todo HLF#59958, ajouter tootip pour expliquer mode actif/hybride
        loading={false} // todo
        onChange={handleChangeJammingMode}
      />
    </VStack>
  );
}
